import { Screen, StackScreenProps } from '@rahtab/stack-navigator';
import { withNumbers } from '@rahtab/system/lib/tools/string';
import {
  BottomSheet,
  Button,
  ButtonBase,
  Card,
  CardCaption,
  CardContainer,
  Colors,
  DatePicker,
  getRadius,
  getSpacing,
  HorizontalLine,
  Picker,
  Text,
  TextInput,
  TextView,
} from '@rahtab/ui';
import AlertIcon from '@rahtab/ui/dist/icons/AlertIcon';
import ChevronLeftIcon from '@rahtab/ui/dist/icons/ChevronLeftIcon';
import LabeledText from 'components/LabeledText';
import type { RootStackType } from 'components/Router';
import useGetInquery, { RequestStatusCode } from 'hooks/useGetInquiry';
import useFindSupplier from 'hooks/useFindSupplier';
import useGetAssignerSupplierList from 'hooks/useGetAssignerSupplierList';
import useSaveInvalidRequest from 'hooks/useSaveInvalidRequest';
import moment from 'moment-jalaali';
import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import useVetApprove from 'hooks/useVetApprove';
import { withSeparator } from '../tools/string';

interface ErrorCardProps {
  title: string;
  message: string;
}

const ErrorCard: React.VFC<ErrorCardProps> = (props) => {
  const { message, title } = props;

  return (
    <Card style={StyleSheet.flatten([styles.card, { marginBottom: 0 }])}>
      <CardContainer>
        <Text style={styles.justifiedText} variant="cardTitle" color="secondary">
          <AlertIcon fill={Colors.secondary} style={styles.cardIcon} /> {'  '}
          {title}
        </Text>
        <Text style={styles.justifiedText} margin="none">
          {message}
        </Text>
      </CardContainer>
    </Card>
  );
};

const DATE_FORMAT = 'YYYY/MM/DD';
const DISPLAY_DATE_FORMAT = 'jYYYY/jMM/jDD';

type SubmitConsigmentInfoProps = StackScreenProps<RootStackType, 'SubmitConsigmentInfo'>;

const SubmitConsigmentInfo: React.FC<SubmitConsigmentInfoProps> = ({ route }) => {
  const { RequestId = '' } = route.params || {};
  const [date, setDate] = useState(moment().format(DATE_FORMAT));
  const [shipCount, setShipmentCount] = useState('');
  const [shiWeight, setShipmentWeight] = useState('');

  const { loading, data } = useGetInquery({ RequestId }, { skip: !RequestId });
  const [request, { loading: submitLoading }] = useVetApprove({
    ShipmentId: RequestId,
    vetApprovedCount: parseInt(shipCount, 10),
    vetApprovedWeight: parseFloat(shiWeight),
  });

  const isInputValid: boolean = !!shipCount && !!shiWeight;

  return (
    <Screen
      title="نتیجه استعلام شناسه مرسوله"
      loading={loading}
      transparentLoading={submitLoading}
      data={data}
      renderControls={({ statusCode }) => {
        const isInvalid = statusCode === RequestStatusCode.Invalid;
        return (
          <TextView
            marginBottom={0}
            borderColor="default"
            valueColor={!isInvalid ? 'primary' : 'secondary'}
            title="شناسه مرسوله استعلام شده"
            value={RequestId}
          />
        );
      }}
      renderMain={({ data: inquryResult, message, comment, statusCode }) => {
        const isApprovable = statusCode === RequestStatusCode.Approvable;
        const isApprovableOrExists = isApprovable || statusCode === RequestStatusCode.Exists;
        const isInvalid = statusCode === RequestStatusCode.Invalid;
        const [plate1, plate2, plate3, plate4] = inquryResult?.plate.split('/') || [];
        return (
          <ScrollView keyboardShouldPersistTaps="handled">
            {!isApprovable && <ErrorCard title={message} message={comment} />}
            {!isInvalid ? (
              <Card style={styles.card}>
                <CardCaption>
                  <Text variant="cardTitle" margin="none" color="primary" style={styles.captionText}>
                    مشخصات مرسوله استعلام شده
                  </Text>
                </CardCaption>
                <CardContainer>
                  <LabeledText label="واحد مرغ داری">{inquryResult?.sourceName}</LabeledText>
                  <LabeledText label="مقدار مرغ زنده وردوی">
                    {withSeparator(inquryResult?.shipmentWeight || '')} کیلوگرم
                  </LabeledText>
                  <LabeledText label="تعداد قطعات مرغ زنده وردوی">
                    {withSeparator(inquryResult?.shipmentCount || '')} قطعه
                  </LabeledText>
                  <LabeledText label="تاریخ تحول ">
                    {moment(inquryResult?.deliveryDate).format(DISPLAY_DATE_FORMAT)}
                  </LabeledText>
                  <LabeledText label="کشتارگاه مقصد">{inquryResult?.destinationName}</LabeledText>
                  <View
                    style={{
                      flexDirection: 'row-reverse',
                      borderWidth: 2,
                      borderRadius: 10,
                      height: 40,
                      overflow: 'hidden',
                      marginBottom: isApprovableOrExists ? getSpacing() : 0,
                    }}>
                    <View style={{ width: 20, backgroundColor: 'rgb(17,48,142)' }} />
                    <View style={{ borderStartWidth: 2, flex: 1 }}>
                      <Text style={{ lineHeight: 36, textAlign: 'center' }} variant="title">
                        {plate1}
                      </Text>
                    </View>
                    <View style={{ borderStartWidth: 2, flex: 1 }}>
                      <Text style={{ lineHeight: 36, textAlign: 'center' }} variant="title">
                        {plate2}
                      </Text>
                    </View>
                    <View
                      style={{
                        borderStartWidth: 2,
                        flex: 1,
                      }}>
                      <Text style={{ lineHeight: 36, textAlign: 'center' }} variant="title">
                        {plate3}
                      </Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ lineHeight: 36, textAlign: 'center' }} variant="title">
                        {plate4}
                      </Text>
                    </View>
                  </View>
                  {isApprovableOrExists && (
                    <>
                      <View>
                        <HorizontalLine />
                        <View style={styles.rowView}>
                          <TextInput
                            value={shiWeight}
                            onChangeText={withNumbers(setShipmentWeight)}
                            placeholder="مقدار مرسوله ورودی را وارد نمایید."
                            style={{ flex: 1 }}
                            keyboardType="numeric"
                          />
                          <TextView style={[styles.textView, { width: 67 }]} value={'کیلوگرم'} />
                        </View>
                        <View style={styles.rowView}>
                          <TextInput
                            value={shipCount}
                            onChangeText={withNumbers(setShipmentCount)}
                            placeholder="تعداد مرسوله ورودی را وارد نمایید."
                            style={{ flex: 1 }}
                            keyboardType="numeric"
                          />
                          <TextView style={[styles.textView, { width: 55 }]} value={'قطعه'} />
                        </View>
                      </View>
                      <DatePicker
                        label="تاریخ وصول"
                        placeholder="تاریخ وصول مرسوله ورودی را وارد نمایید."
                        value={date}
                        onConfirm={setDate}
                        format={DATE_FORMAT}
                        displayFormat={DISPLAY_DATE_FORMAT}
                      />
                      <Button color="primary" text="تایید مرسوله ورودی" onPress={request} disabled={!isInputValid} />
                    </>
                  )}
                </CardContainer>
              </Card>
            ) : null}
          </ScrollView>
        );
      }}
    />
  );
};

export default SubmitConsigmentInfo;

const styles = StyleSheet.create({
  card: {
    margin: getSpacing(),
  },
  cardContainer: {
    borderRadius: getRadius(),
    backgroundColor: Colors.background,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: getSpacing(),
    height: getSpacing('row'),
    justifyContent: 'space-between',
    marginBottom: getSpacing(),
  },
  title: {
    flex: 1,
    marginRight: getSpacing(0.5),
  },
  captionText: {
    textAlign: 'center',
    // flex: 1,
  },
  rowView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  textView: {
    borderColor: Colors.midGray,
    marginStart: getSpacing(),
  },
  justifiedText: {
    textAlign: 'justify',
  },
  cardIcon: {
    marginTop: getSpacing(),
  },
});
